import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetSuppliers,
  CreateSupplier,
  UpdateSupplier,
  DeleteSupplier,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const suppliersQueryKeys = {
  suppliers: () => ["suppliers"] as const,
};

export const invalidateSuppliersQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["suppliers"] });
};

export const useSuppliersQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: suppliersQueryKeys.suppliers(),
    queryFn: async () => unwrapApiResponse(await GetSuppliers()),
  });
};

export const useSuppliersRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: suppliersQueryKeys.suppliers(),
        queryFn: async () => unwrapApiResponse(await GetSuppliers()),
      }),
  });
};

export const useCreateSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateSupplier>[0]) =>
      CreateSupplier(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSuppliersQueries(queryClient);
      }
    },
  });
};

export const useUpdateSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateSupplier>[0]) =>
      UpdateSupplier(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSuppliersQueries(queryClient);
      }
    },
  });
};

export const useDeleteSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteSupplier>[0]) =>
      DeleteSupplier(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSuppliersQueries(queryClient);
      }
    },
  });
};

export const useSuppliersService = (config?: {
  fetchSuppliersEnabled?: boolean;
}) => {
  const suppliersQuery = useSuppliersQuery({
    enabled: config?.fetchSuppliersEnabled !== false,
  });

  const createSupplierMutation = useCreateSupplierMutation();
  const updateSupplierMutation = useUpdateSupplierMutation();
  const deleteSupplierMutation = useDeleteSupplierMutation();

  return {
    suppliersQuery,
    createSupplierMutation,
    updateSupplierMutation,
    deleteSupplierMutation,
  };
};
